import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you would send this to an error tracking service like Sentry
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });

    this.setState({
      error,
      errorInfo
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;

    const prevKeys = prevProps.resetKeys || [];
    const nextKeys = this.props.resetKeys || [];
    const changed =
      prevKeys.length !== nextKeys.length ||
      nextKeys.some((key, index) => key !== prevKeys[index]);

    if (changed) {
      this.handleReset();
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-bg-surface to-bg-surface flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-bg-card rounded-2xl shadow-card p-8 border border-default">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-danger-soft rounded-full">
                  <AlertCircle className="w-12 h-12 text-danger" />
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-3xl font-bold text-center text-txt mb-4">
                Oops! Something went wrong
              </h1>

              {/* Error Description */}
              <p className="text-center text-txt-secondary mb-8">
                We're sorry, but something unexpected happened. Our team has been notified and is working on a fix.
              </p>

              {/* Error Details (Dev Mode Only) */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mb-8 p-4 bg-bg-surface rounded-lg border border-default">
                  <h3 className="text-sm font-semibold text-txt mb-2">
                    Error Details (Development Mode):
                  </h3>
                  <pre className="text-xs text-danger overflow-auto max-h-40 whitespace-pre-wrap break-words">
                    {this.state.error.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <details className="mt-3">
                      <summary className="text-xs font-medium text-txt-secondary cursor-pointer hover:text-txt">
                        Component Stack
                      </summary>
                      <pre className="text-xs text-txt-secondary mt-2 overflow-auto max-h-40 whitespace-pre-wrap break-words">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="flex items-center justify-center px-6 py-3 bg-primary text-primary-contrast rounded-xl hover:bg-bg-card transition-all duration-200 shadow-card hover:shadow-card font-medium"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Try Again
                </button>
                
                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center px-6 py-3 bg-bg-card text-txt border-2 border-default rounded-xl hover:bg-bg-surface transition-all duration-200 font-medium"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Reload Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center px-6 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright transition-all duration-200 shadow-card hover:shadow-card font-medium"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Go Home
                </button>
              </div>

              {/* Help Text */}
              <p className="text-center text-sm text-txt-tertiary mt-8">
                If the problem persists, please contact support at{' '}
                <a href="mailto:support@aideline.com" className="text-accent hover:underline">
                  support@aideline.com
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
